import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DEAL_TYPES, DEAL_STAGES } from "@/lib/deals";

const EMPTY = {
  client_name: "", client_email: "", client_phone: "", lead_id: null, type: "credit", product: "", amount: "",
  stage: "prospect", fees: "", commission: "", scheduled_payment: "", notes: "", next_followup: "",
};
const NUMS = ["amount", "fees", "commission", "scheduled_payment"];
const inputCls = "w-full bg-md-bg border border-md-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-md-secondary";

export default function DealForm({ open, initial, onClose, onSave }) {
  const [d, setD] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const base = { ...EMPTY, ...(initial || {}) };
    NUMS.forEach((k) => { base[k] = base[k] ?? ""; });
    base.next_followup = base.next_followup || "";
    setD(base);
    setError("");
  }, [open, initial]);

  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!d.client_name.trim()) return setError("Le nom du client est obligatoire.");
    const payload = { ...d };
    NUMS.forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
    payload.next_followup = payload.next_followup || null;
    setSaving(true);
    const ok = await onSave(payload);
    setSaving(false);
    if (!ok) setError("Enregistrement impossible. Vérifiez les champs.");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="deal-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-md-primary font-medium">{initial?.id ? "Modifier l'affaire" : "Nouvelle affaire"}</DialogTitle>
          <DialogDescription>{initial?.lead_id ? "Liée à une demande de devis du site." : "Saisie manuelle."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="deal-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Client *"><input data-testid="deal-client-name" value={d.client_name} onChange={set("client_name")} className={inputCls} /></F>
            <F label="Email"><input data-testid="deal-client-email" type="email" value={d.client_email} onChange={set("client_email")} className={inputCls} /></F>
            <F label="Téléphone"><input data-testid="deal-client-phone" value={d.client_phone} onChange={set("client_phone")} className={inputCls} /></F>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Type"><select data-testid="deal-type" value={d.type} onChange={set("type")} className={inputCls}>{Object.entries(DEAL_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
            <F label="Produit / compagnie"><input data-testid="deal-product" value={d.product} onChange={set("product")} placeholder="Ex : PER Generali" className={inputCls} /></F>
            <F label="Étape"><select data-testid="deal-stage" value={d.stage} onChange={set("stage")} className={inputCls}>{Object.entries(DEAL_STAGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></F>
          </div>
          <div className="grid sm:grid-cols-4 gap-3">
            <F label="Montant (€)"><input data-testid="deal-amount" type="number" min="0" step="any" value={d.amount} onChange={set("amount")} className={inputCls} /></F>
            <F label="Frais (€)"><input data-testid="deal-fees" type="number" min="0" step="any" value={d.fees} onChange={set("fees")} className={inputCls} /></F>
            <F label="Commission (€)"><input data-testid="deal-commission" type="number" min="0" step="any" value={d.commission} onChange={set("commission")} className={inputCls} /></F>
            <F label="Versements prog. (€/mois)"><input data-testid="deal-scheduled-payment" type="number" min="0" step="any" value={d.scheduled_payment} onChange={set("scheduled_payment")} className={inputCls} /></F>
          </div>
          <F label="Date de relance"><input data-testid="deal-next-followup" type="date" value={d.next_followup} onChange={set("next_followup")} className={`${inputCls} sm:max-w-[220px]`} /></F>
          <F label="Notes"><textarea data-testid="deal-notes" rows={3} value={d.notes} onChange={set("notes")} className={inputCls} /></F>
          {error && <p data-testid="deal-form-error" className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm text-md-muted hover:text-md-primary">Annuler</button>
            <button data-testid="deal-save-btn" disabled={saving} className="bg-md-secondary hover:bg-md-secondaryHover disabled:opacity-50 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-md-muted font-semibold mb-1.5 block">{label}</span>
    {children}
  </label>
);
